import { Search, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { useState, useRef, useEffect, ReactNode } from "react";

interface SearchInputProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onSearch: (value: string) => void;
  maxLines?: number; // Maximum number of lines before scrolling
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  defaultValue?: string;
  actions?: ReactNode; // Optional action buttons to render inside the input
}

export function SearchInput({
  className,
  onSearch,
  maxLines = 5, // Default to 5 lines max before scrolling
  value,
  onChange,
  defaultValue,
  actions,
  ...props
}: SearchInputProps) {
  const [showArrow, setShowArrow] = useState(Math.random() < 0.5);
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<{ start: number; end: number } | null>(null);

  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue;

  // Calculate line height based on the first line
  const getLineHeight = (): number => {
    const textarea = textareaRef.current;
    if (!textarea) return 20; // Default fallback

    // Temporarily set single line of text to measure
    const originalValue = textarea.value;
    const originalHeight = textarea.style.height;
    textarea.value = "X";
    textarea.style.height = "auto";
    const singleLineHeight = textarea.scrollHeight;

    // Restore original values
    textarea.value = originalValue;
    textarea.style.height = originalHeight;

    return singleLineHeight;
  };

  // Save the current cursor position
  const saveCursorPosition = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      cursorPositionRef.current = {
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
      };
    }
  };

  // Restore the saved cursor position
  const restoreCursorPosition = () => {
    const textarea = textareaRef.current;
    const position = cursorPositionRef.current;
    if (textarea && position) {
      textarea.setSelectionRange(position.start, position.end);
    }
  };

  // Function to adjust the height of textarea with max height limit
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Save cursor position before adjusting height
      saveCursorPosition();

      const lineHeight = getLineHeight();
      const maxHeight = lineHeight * maxLines;

      // Reset height first to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate new height
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${Math.max(newHeight, 56)}px`;

      // Add scrolling if content exceeds max height
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? "auto" : "hidden";

      // Restore cursor position
      setTimeout(restoreCursorPosition, 0);
    }
  };

  // Adjust height on initial render and when value changes
  useEffect(() => {
    adjustHeight();
  }, [currentValue, maxLines]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(e);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentValue.trim()) {
      onSearch(currentValue);
    }
  };

  // Handle Enter key to submit form, but allow Shift+Enter for new lines
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (currentValue.trim()) {
        onSearch(currentValue);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn("relative w-full", className)}>
      <div className="relative group rounded-2xl p-[1.5px] bg-gradient-to-br from-brand-accent to-brand-teal shadow-[0_10px_40px_-12px_rgba(116,71,150,0.35)] transition-shadow focus-within:shadow-[0_18px_55px_-12px_rgba(72,178,205,0.45)]">
        <div className="rounded-[14px] bg-brand-bg">
          {/* Input row */}
          <div className="relative flex">
            <textarea
              ref={textareaRef}
              name="search"
              placeholder="Comment pouvons-nous vous aider ?"
              className="min-h-[56px] max-h-[200px] py-4 resize-none w-full bg-transparent pl-5 pr-16 text-base text-brand-text placeholder-brand-muted transition-all focus:outline-none scrollbar-thin scrollbar-thumb-brand-muted/30 scrollbar-track-transparent"
              value={currentValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onClick={saveCursorPosition}
              onKeyUp={saveCursorPosition}
              rows={1}
              {...props}
            />
            <button
              type="submit"
              aria-label="Envoyer"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2.5 rounded-full transition-all bg-gradient-to-br from-brand-accent to-brand-teal text-white shadow-[0_6px_18px_-6px_rgba(116,71,150,0.55)] hover:shadow-[0_10px_24px_-6px_rgba(72,178,205,0.6)] hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-teal/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              disabled={!currentValue.trim() || (props as any).disabled}
            >
              {showArrow ? (
                <ArrowRight className="h-5 w-5" />
              ) : (
                <Search className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Action buttons row */}
          {actions && (
            <div className="px-3 pb-3 pt-1 flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
